import React, { useState, useEffect } from "react";
import { MdInfoOutline } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton";
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors";
import { MobergFontFamily, MobergFontSize } from "../../../../Components/MobergFont/MobergFont";

const StartMonitoringModal = ({ 
    isOpen, 
    gatewayId,
    gatewayInfo,
    patientId, 
    patientDetails,
    onConfirm, 
    onCancel,
    endpointProvider,
    LINKS,
    authProvider,
    siteId,
    projectId
}) => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setLoading(false);
            setError(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const mrn = patientDetails?.mrn || patientId || "Not Assigned";
    const firstName = patientDetails?.firstName || null;
    const lastName = patientDetails?.lastName || null;
    const gatewayIdValue = gatewayId || "Not Assigned";
    const mcpDeviceName = gatewayInfo?.gateway_name || "MCP Device 1";

    const handleConfirm = async () => {
        setLoading(true);
        setError(null);

        try {
            // Create upload
            const body = {
                project_id: projectId,
                site_id: siteId,
                patient_id: patientId,
                user_id: authProvider?.currentUser?.id,
                mrn: mrn
            };

            if (firstName) {
                body.first_name = firstName;
            }
            if (lastName) {
                body.last_name = lastName;
            }

            console.log("StartMonitoringModal - Creating upload with body:", body);
            const resUpload = await endpointProvider.post(LINKS.DATA.UPLOAD.CREATE_MONITORING_UPLOAD, body);
            const uploadId = resUpload?.upload_id || null;

            if (!uploadId) {
                throw new Error(`Failed to upload data for patient ${patientId} on gateway ${gatewayIdValue}`);
            }

            // Start monitoring
            await endpointProvider.post(LINKS.AGGREGATOR.START_MONITORING, {
                upload_id: uploadId,
                gateway_id: parseInt(gatewayIdValue, 10)
            });

            console.log("StartMonitoringModal - Successfully started monitoring");
            
            // Call the onConfirm callback to navigate to monitoring page
            if (onConfirm) {
                onConfirm();
            }
        } catch (err) {
            console.error("StartMonitoringModal - Error starting monitoring:", err);
            setError(err.message || "Failed to start monitoring");
            setLoading(false);
        }
    };

    // Format patient name for display
    const patientName = firstName || lastName 
        ? `${firstName || ''} ${lastName || ''}`.trim() 
        : "-";

    return (
        <>
            {/* Overlay */}
            <div 
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
                onClick={loading ? undefined : onCancel}
            >
                {/* Modal */}
                <div 
                    style={{ 
                        textAlign: "center", 
                        padding: "20px", 
                        width: "425px",
                        backgroundColor: "var(--color-surface-panel)",
                        borderRadius: "8px",
                        boxShadow: "var(--modal-box-shadow)"
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MdInfoOutline size={81} style={{ color: "var(--color-accent-interactive)", marginBottom: "5px" }} />

                    <h1 style={{ 
                        fontFamily: MobergFontFamily.HEADER, 
                        fontStyle: "normal", 
                        fontWeight: "700", 
                        fontSize: "24px", 
                        lineHeight: "110%", 
                        color: "var(--color-text-primary)",
                        marginBottom: "10px"
                    }}>
                        Start monitoring
                    </h1>

                    {error && (
                        <p style={{ 
                            textAlign: "center", 
                            margin: "0 0 10px 0", 
                            padding: "5px 15px", 
                            color: "red",
                            fontSize: "14px",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Error: {error}
                        </p>
                    )}

                    {loading ? (
                        <div style={{ padding: "20px 15px", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "10px" }}>
                            <CircularProgress size={40} />
                            <p style={{ 
                                margin: 0, 
                                fontSize: "16px",
                                fontFamily: MobergFontFamily.REGULAR,
                                color: "var(--text-secondary-color)"
                            }}>
                                Starting monitoring...
                            </p>
                        </div>
                    ) : (
                        <>
                            <div style={{
                                textAlign: "left",
                                padding: "20px 15px",
                                backgroundColor: "var(--color-input-bg)",
                                borderRadius: "4px",
                                margin: "15px 0"
                            }}>
                                <p style={{
                                    margin: "0 0 10px 0",
                                    fontSize: "15px",
                                    fontFamily: MobergFontFamily.REGULAR,
                                    color: "var(--color-text-primary)",
                                    lineHeight: "1.6"
                                }}>
                                    <strong>MCP Device:</strong> {mcpDeviceName}
                                </p>
                                <p style={{
                                    margin: "0 0 10px 0",
                                    fontSize: "15px",
                                    fontFamily: MobergFontFamily.REGULAR,
                                    color: "var(--color-text-primary)",
                                    lineHeight: "1.6"
                                }}>
                                    <strong>Patient ID:</strong> {mrn}
                                </p>
                                <p style={{
                                    margin: "0",
                                    fontSize: "15px",
                                    fontFamily: MobergFontFamily.REGULAR,
                                    color: "var(--color-text-primary)",
                                    lineHeight: "1.6"
                                }}>
                                    <strong>Patient name:</strong> {patientName}
                                </p>
                            </div>

                            <p style={{
                                textAlign: "center",
                                padding: "0 15px 10px",
                                margin: 0,
                                fontSize: "15px",
                                fontFamily: MobergFontFamily.REGULAR,
                                color: "var(--text-secondary-color)",
                                lineHeight: "1.5"
                            }}>
                                Are you sure you want to start monitoring for this patient?
                            </p>
                        </>
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "20px" }}>
                        <MobergButton 
                            disabled={loading} 
                            shape={MobergButtonShape.FULL_WIDTH}
                            fontSize={MobergFontSize.LARGE} 
                            theme={MobergTheme.BLUE}
                            variant={MobergButtonVariant.FILLED} 
                            onClick={handleConfirm}
                            style={{height: "55px", fontFamily: MobergFontFamily.HEADER}}>
                            Start Monitoring
                        </MobergButton>

                        <MobergButton 
                            disabled={loading} 
                            shape={MobergButtonShape.FULL_WIDTH}
                            fontSize={MobergFontSize.LARGE}
                            theme={MobergTheme.BLUE}
                            variant={MobergButtonVariant.OUTLINE} 
                            onClick={onCancel}
                            style={{height: "55px", fontFamily: MobergFontFamily.HEADER}}>
                            Cancel
                        </MobergButton>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StartMonitoringModal;

